import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fromInvite, setFromInvite] = useState(false);

  // Prefill from invite-email link: /login?email=...&temp=...
  useEffect(() => {
    const qEmail = params.get("email") || "";
    const qTemp = params.get("temp") || "";
    if (qEmail || qTemp) {
      setEmail(qEmail);
      setPassword(qTemp);
      setFromInvite(!!qTemp);
      // Strip the temp password from the browser URL so it isn't bookmarked
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.user.name}`);
      if (data.user.is_admin) navigate("/admin");
      else if (!data.has_access) navigate("/paywall");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="block mb-12 text-center" data-testid="login-brand-link">
          <img src="/gcoms-logo.png" alt="GCOMS" className="w-16 h-16 mx-auto rounded-full bg-black p-1" />
          <div className="font-heading text-2xl font-bold tracking-tight mt-3">GCOMS.RN</div>
        </Link>
        <div className="module-card p-10 bg-white">
          <div className="uppercase-label">Member sign in</div>
          <h1 className="font-heading text-3xl font-semibold mt-3">Welcome back.</h1>

          {fromInvite && (
            <div
              data-testid="login-invite-notice"
              className="mt-5 flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-[#FFF7F0] border border-[#FDE6D8] text-xs"
            >
              <Sparkles size={14} className="text-[#F26A24] mt-0.5 shrink-0" />
              <div className="text-ink/80">
                We've filled in your temporary password from the invite link. After signing in you'll be asked to set your own password.
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-md bg-ink text-bone font-medium hover:bg-ink/90 disabled:opacity-60 transition-colors"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
            <div className="text-right -mt-1">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-xs text-muted-foreground hover:text-ink underline-offset-4 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </form>
          <div className="mt-6 text-sm text-muted-foreground text-center">
            No account? <Link to="/signup" data-testid="login-signup-link" className="text-ink underline">Request access</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
